import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect, forwardRef, useImperativeHandle } from "react";
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link as LinkIcon,
  Heading2, Heading3, Pilcrow, Code, Undo, Redo, Quote,
} from "lucide-react";

const TbBtn = ({ active, onClick, title, children, testid }) => (
  <button type="button" onClick={onClick} title={title} data-testid={testid}
    className={`p-1.5 rounded hover:bg-zinc-700 transition-colors ${active ? "bg-blue-600 text-white" : "text-zinc-300"}`}>
    {children}
  </button>
);

const TiptapEditor = forwardRef(function TiptapEditor(
  { value, onChange, placeholder = "" },
  ref
) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-blue-400 underline" } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "tiptap-content prose prose-invert prose-sm max-w-none min-h-[260px] px-3 py-2 focus:outline-none text-zinc-100",
      },
    },
    onUpdate: ({ editor: e }) => onChange?.(e.getHTML()),
  });

  // Keep editor content in sync if parent replaces value (e.g., variable insert from outside)
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useImperativeHandle(ref, () => ({
    insertText: (text) => {
      if (!editor) return;
      editor.chain().focus().insertContent(text).run();
    },
    focus: () => editor?.commands.focus(),
  }), [editor]);

  if (!editor) return null;

  function setLink() {
    const url = window.prompt("URL eingeben:", editor.getAttributes("link").href || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }

  return (
    <div className="border border-zinc-800 rounded-md bg-zinc-950 overflow-hidden" data-testid="tiptap-editor">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-zinc-800 bg-zinc-900">
        <TbBtn testid="tt-bold" active={editor.isActive("bold")}     onClick={() => editor.chain().focus().toggleBold().run()}     title="Fett"><Bold className="w-3.5 h-3.5" /></TbBtn>
        <TbBtn testid="tt-italic" active={editor.isActive("italic")}   onClick={() => editor.chain().focus().toggleItalic().run()}   title="Kursiv"><Italic className="w-3.5 h-3.5" /></TbBtn>
        <TbBtn testid="tt-underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Unterstrichen"><UnderlineIcon className="w-3.5 h-3.5" /></TbBtn>
        <div className="w-px h-5 bg-zinc-800 mx-1" />
        <TbBtn testid="tt-h2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Überschrift"><Heading2 className="w-3.5 h-3.5" /></TbBtn>
        <TbBtn testid="tt-h3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Unter-Überschrift"><Heading3 className="w-3.5 h-3.5" /></TbBtn>
        <TbBtn testid="tt-p" active={editor.isActive("paragraph") && !editor.isActive("heading")} onClick={() => editor.chain().focus().setParagraph().run()} title="Absatz"><Pilcrow className="w-3.5 h-3.5" /></TbBtn>
        <div className="w-px h-5 bg-zinc-800 mx-1" />
        <TbBtn testid="tt-bullet" active={editor.isActive("bulletList")}  onClick={() => editor.chain().focus().toggleBulletList().run()}  title="Aufzählung"><List className="w-3.5 h-3.5" /></TbBtn>
        <TbBtn testid="tt-ordered" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Nummerierung"><ListOrdered className="w-3.5 h-3.5" /></TbBtn>
        <TbBtn testid="tt-quote" active={editor.isActive("blockquote")}  onClick={() => editor.chain().focus().toggleBlockquote().run()}  title="Zitat"><Quote className="w-3.5 h-3.5" /></TbBtn>
        <TbBtn testid="tt-code" active={editor.isActive("codeBlock")}    onClick={() => editor.chain().focus().toggleCodeBlock().run()}    title="Code-Block"><Code className="w-3.5 h-3.5" /></TbBtn>
        <div className="w-px h-5 bg-zinc-800 mx-1" />
        <TbBtn testid="tt-link" active={editor.isActive("link")} onClick={setLink} title="Link"><LinkIcon className="w-3.5 h-3.5" /></TbBtn>
        <div className="ml-auto flex items-center gap-0.5">
          <TbBtn testid="tt-undo" onClick={() => editor.chain().focus().undo().run()} title="Rückgängig"><Undo className="w-3.5 h-3.5" /></TbBtn>
          <TbBtn testid="tt-redo" onClick={() => editor.chain().focus().redo().run()} title="Wiederholen"><Redo className="w-3.5 h-3.5" /></TbBtn>
        </div>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
});

export default TiptapEditor;
